import { buildColumn, ColumnsProps, filterOption, noneDecimalFormatter, useDispatch } from '@/Helper';
import { deviceAdminApi, enrollmentApi } from '@/api';
import { Select } from '@/components/common';
import { DoorMasterModal } from '@/components/devicecontrol/VelocityConfiguration/DoorGroup/DoorMasterModal/DoorMasterModal';
import { DoorModal } from '@/components/devicecontrol/VelocityConfiguration/DoorGroup/DoorModal/DoorModal';
import { FunctionGroupModal } from '@/components/devicecontrol/VelocityConfiguration/FunctionGroup/FunctionGroupModal/FunctionGroupModal';
import { getPermissionErrorMessage, SecuredComponents, User } from '@/model/AccountModel';
import { BaseColumns } from '@/model/CommonModel';
import { ControlZone, DoorGroup, SelectOption } from '@/model/DeviceAdminModel';
import {
	ConflictMessage,
	CredentialFunction,
	CredentialFunctionCategory,
	FunctionCategory,
	FunctionGroup,
	FunctionModel,
	PanelZone,
	ThreatLevel,
} from '@/model/EnrollmentModel';
import { Logger } from '@/model/LoggingModel';
import { GreenEyeIcon, TrashCircleIcon } from '@/resources/Icons';
import { StoreEvent } from '@/store';
import { DeviceControlAction, setDefaultTablePaginationSetting } from '@/store/deviceControl/actions';
import { MinusSquareOutlined, PlusSquareOutlined } from '@ant-design/icons';
import { Button, InputNumber, Spin, Table } from 'antd';
import cx from 'classnames';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { batch } from 'react-redux';
import { ThunkAction } from 'redux-thunk';
import { filterDoorGroups, filterLocalControllers, filterLocalFunctionGroups } from '../../Helper';
import {
	setControlZonesArrayAction,
	setCredentialFunctionsArrayAction,
	setFunctionTabDgs,
	setFunctionTabFgs,
	setFunctionTabServicesDataAction,
	setRootModelPropertyAction,
} from '../../credentialTemplateContext/actions';
import { CredentialStoreContext } from '../../credentialTemplateContext/context';
import styles from '../../credentialmodal.module.scss';
import { ControllerConflictWarningModal } from './ControllerConflictWarningModal/ControllerConflictWarningModal';
import { DoorGroupInfoTable } from './DoorGroupInfoTable';
import { MasterDoorGroupInfoModal } from './MasterDoorGroupInfoModal/MasterDoorGroupInfoModal';

enum CredentialFunctionEnum {
	CONTROL_ZONE,
	DOOR_GROUP,
	FUNCTION_GROUP,
	THREAT_LEVEL,
	INDEXED_COMMAND,
	DEADMAN_TIMER,
	SPECIAL_DOOR_GROUP,
}
enum DoorModalType {
	None = -1,
	DoorGroup = -2,
	MasterDoorGroup = -3,
}
type CredentialFunctionsDataType = CredentialFunction & BaseColumns;
type AddCredentialFunction = {
	credentialFunction: CredentialFunctionEnum;
	controlZoneIndex?: number;
	controllerIndex?: number;
};
type MasterDoorGroupInfo = {
	id: number;
	name: string;
	visible: boolean;
};

const defaultMasterDoorGroupInfo: MasterDoorGroupInfo = { visible: false, id: 0, name: '' };
const MAX_DEADMAN_SECONDS: number = 65000;
const user: User = getUser();
const noOfCustomAccessZones: number = user.noOfCustomAccessZones;
const doorGroupComponentPermission = User.getComponentPermission(user, SecuredComponents.Door_Groups);
const masterDoorGroupComponentPermission = User.getComponentPermission(user, SecuredComponents.Master_Door_Group);
const functionGroupComponentPermission = User.getComponentPermission(user, SecuredComponents.Function_Group);

const defFgs: FunctionGroup[] = [{ ControllerIds: [], Defined: false, FunctionGroupId: -1, Name: '' }];
const defDgs: DoorGroup[] = [
	{
		ControllerIds: [],
		DoorGroupId: -1,
		IsMasterDoorGroup: false,
		Name: '',
		HasObjectPermission: false,
	},
];
if (functionGroupComponentPermission.canAdd) {
	defFgs.push({ ControllerIds: [], Defined: false, FunctionGroupId: -2, Name: `<${_('AddFunctionGroup')}>` });
}
if (doorGroupComponentPermission.canAdd) {
	defDgs.push({
		ControllerIds: [],
		DoorGroupId: -2,
		IsMasterDoorGroup: false,
		Name: `<${_('AddDoorGroup')}>`,
		HasObjectPermission: false,
	});
}
if (masterDoorGroupComponentPermission.canAdd) {
	defDgs.push({
		ControllerIds: [],
		DoorGroupId: -3,
		IsMasterDoorGroup: false,
		Name: `<${_('AddMasterDoorGroup')}>`,
		HasObjectPermission: false,
	});
}

const timeColumnName = `${_('Time')} (${_('Sec')})`;

const CredentialTemplateModalFunctionsTab: React.FC = () => {
	//#region state management variables
	const deviceCtrlDispatcher = useDispatch<StoreEvent | ThunkAction<Promise<void>, {}, {}, DeviceControlAction>>();
	const {
		credentialInitialState: {
			configData: { CCMVersionSupportsHRecord, CustomAccessZones, IsDeviceAdminCredentialTemplate, OverlapAccessFlag, IndexedCommandsOptions },
			credentialMessages: { DeleteCZNotAllowed, DeleteFunctionNotAllowed, EditFunctionNotAllowed },
			fetchedCredential,
			functionsTabState: { controllers, controlZones, credentialFunctions, doorGroups, functionCategories, functionGroups },
			generalTabState: {
				IDF,
				informationSectionState: { credentialTemplates, linkToCredential },
			},
			modelEstablished,
			limitsTabState: { threatLevels },
		},
		dispatcher,
	} = useContext(CredentialStoreContext);
	const [controller, setController] = useState<number>(-1);
	const [controlZone, setControlZone] = useState<number>(-1);
	const [doorGroup, setDoorGroup] = useState<string>('');
	const [localDoorGroups, setLocalDoorGroups] = useState<DoorGroup[]>([]);
	const [functionCategory, setFunctionCategory] = useState<number>(-1);
	const [functionGroup, setFunctionGroup] = useState<number>(-1);
	const [functionS, setFunctionS] = useState<number>(-1);
	const [showConflictMsgModal, setShowConflictMsgModal] = useState<boolean>(false);
	const [conflictMessages, setConflictMessages] = useState<ConflictMessage[]>([]);
	const [fetchedControlZones, setFetchedControlZones] = useState<Map<number, ControlZone[]>>();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isTabDisabled, setIsTabDisabled] = useState<boolean>(false);
	const [deadmanTime, setDeadmanTime] = useState<number>(0);
	const [indexedCommandId, setIndexedCommandId] = useState<number>(1);
	const [threatLevel, setThreatLevel] = useState<number>(0);
	const [doorModal, setDoorModal] = useState<DoorModalType>(DoorModalType.None);
	const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
	const [functionGroupModal, setFunctionGroupModal] = useState<boolean>(false);
	const [localFunctionGroups, setLocalFunctionGroups] = useState<FunctionGroup[]>([]);
	const [masterDoorGroupInfo, setMasterDoorGroupInfo] = useState<MasterDoorGroupInfo>(defaultMasterDoorGroupInfo);
	const [tableMaxHeight, setTableMaxHeight] = useState<number>(450);
	const catContainerRef = useRef<HTMLDivElement>(null);
	const ddContainerRef = useRef<HTMLDivElement>(null);
	const outerContainerRef = useRef<HTMLDivElement>(null);
	//#endregion

	useEffect(() => {
		setIsLoading(true);
		Promise.all([
			enrollmentApi.getFunctionCategories(),
			enrollmentApi.getOperatorDoorGroups(),
			enrollmentApi.getOperatorFunctionGroups(),
			enrollmentApi.getXboxControllers(),
		])
			.then(res => {
				const [functionCategories, doorGroups, functionGroups, controllers] = res;
				const dgs: DoorGroup[] = doorGroups?.length > 0 ? [...defDgs, ...doorGroups.sort((a, b) => a.Name.localeCompare(b.Name))] : defDgs;
				const fgs: FunctionGroup[] = functionGroups?.length > 0 ? [...defFgs, ...functionGroups] : [...defFgs];
				dispatcher(
					setFunctionTabServicesDataAction({
						controllers,
						doorGroups: dgs,
						functionCategories,
						functionGroups: fgs,
					})
				);
				if (functionCategories?.length > 0) setFunctionCategory(functionCategories[0].FunctionCategoryIndex);
				if (credentialFunctions?.length > 0) {
					setLocalDoorGroups(filterDoorGroups(credentialFunctions, dgs));
					setLocalFunctionGroups(filterLocalFunctionGroups(credentialFunctions, fgs));
				} else {
					setLocalFunctionGroups(fgs);
					setLocalDoorGroups(dgs);
				}
				setIsLoading(false);
			})
			.catch(err => {
				Logger.writeErrorLog(err);
				dispatcher(
					setFunctionTabServicesDataAction({
						controllers: [],
						doorGroups: [],
						functionCategories: [],
						functionGroups: [],
					})
				);
			});
	}, []);

	useEffect(() => {
		const updateMaxHeight = () => {
			if (outerContainerRef.current && catContainerRef.current && ddContainerRef.current) {
				const height = outerContainerRef.current.offsetHeight - catContainerRef.current.offsetHeight - ddContainerRef.current.offsetHeight - 70;
				const tableBody = outerContainerRef.current.querySelector('.ant-table-body') as HTMLElement;
				if (tableBody) {
					tableBody.style.height = `${height}px`;
					const placeholder = outerContainerRef.current.querySelector('.ant-table-placeholder') as HTMLElement;
					if (placeholder) {
						placeholder.style.height = `${height}px`;
					}
				}

				setTableMaxHeight(Math.round(height));
			}
		};

		const resizeObserver = new ResizeObserver(updateMaxHeight);
		if (catContainerRef.current) resizeObserver.observe(catContainerRef.current);
		if (ddContainerRef.current) resizeObserver.observe(ddContainerRef.current);

		window.addEventListener('resize', updateMaxHeight);
		return () => {
			resizeObserver.disconnect();
			window.removeEventListener('resize', updateMaxHeight);
		};
	}, [JSON.stringify(credentialFunctions)]);

	useEffect(() => {
		if (functionCategories?.length > 0) {
			const selectedFunctionCategory = functionCategories[0];
			setFunctionCategory(selectedFunctionCategory.FunctionCategoryIndex);
			handleOnChangeFunction(selectedFunctionCategory.Functions[0].FunctionIndex?.toString(), selectedFunctionCategory.FunctionCategoryIndex);
		}
	}, [JSON.stringify(functionCategories)]);

	useEffect(() => {
		if (functionCategories?.length > 0 && doorGroup) {
			if (functionCategory == 4 && (functionS === 25 || functionS === 26 || functionS === 27)) {
				addCredentialFunction({
					credentialFunction: CredentialFunctionEnum.SPECIAL_DOOR_GROUP,
				});
			} else {
				addCredentialFunction({
					credentialFunction: CredentialFunctionEnum.DOOR_GROUP,
				});
			}
		}
	}, [doorGroup]);

	useEffect(() => {
		if (functionCategories?.length > 0 && functionGroup > -1) {
			addCredentialFunction({
				credentialFunction: CredentialFunctionEnum.FUNCTION_GROUP,
			});
		}
	}, [functionGroup]);

	useEffect(() => {
		setIsTabDisabled(false);
		if (IsDeviceAdminCredentialTemplate && IDF === 0) setIsTabDisabled(true);
		else if (linkToCredential) setIsTabDisabled(true);
		else if (IDF === 0) setIsTabDisabled(true);
	}, [IDF]);

	useEffect(() => {
		linkToChkChanged();
	}, [linkToCredential]);

	useEffect(() => {
		if (fetchedCredential && modelEstablished) {
			setLocalDoorGroups(filterDoorGroups(credentialFunctions, doorGroups));
		}
	}, [fetchedCredential, modelEstablished]);

	const addCredentialFunction = ({ credentialFunction, controlZoneIndex = -1, controllerIndex = -1 }: AddCredentialFunction) => {
		const selectedFunctionCategory = functionCategories.find(fc => fc.FunctionCategoryIndex === functionCategory);
		const selectedFunction = selectedFunctionCategory.Functions.find(f => f.FunctionIndex === functionS);
		const newTableData: Partial<CredentialFunction>[] = credentialFunctions ? [...credentialFunctions] : [];
		switch (credentialFunction) {
			case CredentialFunctionEnum.DOOR_GROUP:
			case CredentialFunctionEnum.SPECIAL_DOOR_GROUP:
				if (doorGroup && doorGroups?.length > 0) {
					const [_, strDoorGroupId, strIsMaster] = doorGroup.split('%');
					const doorGroupId = parseInt(strDoorGroupId);
					const boolIsMaster = strIsMaster === 'true';
					const selectedDoorGroup = doorGroups.find(dg => dg.DoorGroupId === doorGroupId && dg.IsMasterDoorGroup === boolIsMaster);
					const newItem = {
						key: buildUniqueIdDoorGroup(credentialFunction, selectedDoorGroup),
						controllerIds: selectedDoorGroup.ControllerIds,
						functionModel: selectedFunction,
						functionCategory: selectedFunctionCategory,
					};

					if (selectedDoorGroup && doorGroupId > -1 && !checkConflicts(newTableData, newItem)) {
						newTableData.push({
							key: newItem.key,
							FunctionIndex: newItem.functionModel.FunctionIndex,
							FunctionName: newItem.functionModel.DefaultFunctionName,
							FunctionCategory: newItem.functionCategory.FunctionCategoryIndex,
							ControllerIds: newItem.controllerIds,
							Name: selectedDoorGroup.Name,
							DisplayName: selectedDoorGroup.Name,
							HostZone: selectedDoorGroup.IsMasterDoorGroup ? -1 : selectedDoorGroup.DoorGroupId,
							MasterDoorGroupId: selectedDoorGroup.IsMasterDoorGroup ? selectedDoorGroup.DoorGroupId : -1,
							Zones: undefined,
							IsMasterDoorGroup: selectedDoorGroup.IsMasterDoorGroup,
							HasObjectPermission: selectedDoorGroup.HasObjectPermission,
						});
						dispatcher(setCredentialFunctionsArrayAction(newTableData));
					}

					const tempDgs = filterDoorGroups(newTableData, doorGroups);
					setLocalDoorGroups(tempDgs);
				}
				setDoorGroup('');
				break;

			case CredentialFunctionEnum.FUNCTION_GROUP:
				if (functionGroup > 0 && functionGroups?.length > 0) {
					const selectedFunctionGroup = functionGroups.find(fg => fg.FunctionGroupId === functionGroup);
					if (selectedFunctionGroup) {
						const { ControllerIds: controllerIds, FunctionGroupId, Name } = selectedFunctionGroup;
						const newItem = {
							key: buildUniqueIdFunctionGroup(credentialFunction, selectedFunctionGroup),
							controllerIds,
							functionModel: selectedFunction,
							functionCategory: selectedFunctionCategory,
						};
						if (!checkConflicts(newTableData, newItem)) {
							newTableData.push({
								key: newItem.key,
								FunctionIndex: newItem.functionModel.FunctionIndex,
								FunctionName: newItem.functionModel.DefaultFunctionName,
								FunctionCategory: newItem.functionCategory.FunctionCategoryIndex,
								ControllerIds: newItem.controllerIds,
								Name,
								DisplayName: Name,
								HostZone: FunctionGroupId,
								MasterDoorGroupId: -1,
								IsMasterDoorGroup: false,
								HasObjectPermission: true,
								Zones: undefined,
							});
							dispatcher(setCredentialFunctionsArrayAction(newTableData));
						}
					}
					const newLocalGroup = filterLocalFunctionGroups(newTableData, functionGroups);
					setLocalFunctionGroups(newLocalGroup);
				}
				setFunctionGroup(-1);
				break;

			case CredentialFunctionEnum.INDEXED_COMMAND:
				if (controllers?.length > 0) {
					const indexedCommandOption = IndexedCommandsOptions.find(option => option.Id === indexedCommandId);
					const cController = controllers.find(c => c.ControllerId === controllerIndex);
					if (cController && indexedCommandOption) {
						const { ControllerId } = cController;
						const newItem = {
							key: buildUniqueIdIndexedCommand(credentialFunction, indexedCommandOption),
							controllerIds: [ControllerId],
							functionModel: selectedFunction,
							functionCategory: selectedFunctionCategory,
						};
						if (!checkConflicts(newTableData, newItem)) {
							newTableData.push({
								key: newItem.key,
								FunctionIndex: newItem.functionModel.FunctionIndex,
								FunctionName: newItem.functionModel.DefaultFunctionName,
								FunctionCategory: newItem.functionCategory.FunctionCategoryIndex,
								ControllerIds: newItem.controllerIds,
								Name: indexedCommandOption.Name,
								DisplayName: `${indexedCommandOption.Name} (${cController.ControllerName})`,
								HostZone: -1,
								MasterDoorGroupId: -1,
								IsMasterDoorGroup: false,
								HasObjectPermission: true,
								Zones: [
									{
										ControllerId,
										ZoneIndex: indexedCommandId,
										DeadmanTime: undefined,
									},
								],
							});
							dispatcher(setCredentialFunctionsArrayAction(newTableData));
						}
					}
				}
				setController(-1);
				break;

			case CredentialFunctionEnum.THREAT_LEVEL:
				if (controllers?.length > 0 && threatLevels?.length > 0) {
					const threatLevelOption = threatLevels.find(option => option.ThreatId === threatLevel);
					const cController = controllers.find(c => c.ControllerId === controllerIndex);
					if (cController && threatLevelOption) {
						const { ControllerId } = cController;
						const newItem = {
							key: buildUniqueIdThreatLevel(credentialFunction, threatLevelOption),
							controllerIds: [ControllerId],
							functionModel: selectedFunction,
							functionCategory: selectedFunctionCategory,
						};
						if (!checkConflicts(newTableData, newItem)) {
							newTableData.push({
								key: newItem.key,
								FunctionIndex: newItem.functionModel.FunctionIndex,
								FunctionName: newItem.functionModel.DefaultFunctionName,
								FunctionCategory: newItem.functionCategory.FunctionCategoryIndex,
								ControllerIds: newItem.controllerIds,
								Name: threatLevelOption.Name,
								DisplayName: `${threatLevelOption.Name} (${cController.ControllerName})`,
								HostZone: -1,
								MasterDoorGroupId: -1,
								IsMasterDoorGroup: false,
								HasObjectPermission: true,
								Zones: [
									{
										ControllerId,
										ZoneIndex: threatLevel,
										DeadmanTime: undefined,
									},
								],
							});
							dispatcher(setCredentialFunctionsArrayAction(newTableData));
						}
					}
				}
				setController(-1);
				break;

			default:
				if (controlZoneIndex > 0 && controllers?.length > 0 && controlZones?.length > 0) {
					const cControlZone = controlZones.find(cz => cz.ControlZoneIndex === controlZoneIndex);
					const cController = controllers.find(c => c.ControllerId === controllerIndex);
					if (cController && cControlZone) {
						const { ControlZoneIndex, ControlZoneName } = cControlZone;
						const { ControllerId } = cController;
						const newItem = {
							key: buildUniqueIdDefault(credentialFunction, ControlZoneIndex, ControllerId),
							controllerIds: [ControllerId],
							functionModel: selectedFunction,
							functionCategory: selectedFunctionCategory,
						};
						if (!checkConflicts(newTableData, newItem)) {
							const deadmanTimeSec: number = credentialFunction === CredentialFunctionEnum.DEADMAN_TIMER ? deadmanTime : undefined;
							newTableData.push({
								key: newItem.key,
								FunctionIndex: newItem.functionModel.FunctionIndex,
								FunctionName: newItem.functionModel.DefaultFunctionName,
								FunctionCategory: newItem.functionCategory.FunctionCategoryIndex,
								ControllerIds: newItem.controllerIds,
								Name: ControlZoneName,
								DisplayName: `${ControlZoneName} (${cController.ControllerName})`,
								HostZone: -1,
								MasterDoorGroupId: -1,
								IsMasterDoorGroup: false,
								HasObjectPermission: true,
								Zones: [
									{
										ControllerId,
										ZoneIndex: ControlZoneIndex,
										DeadmanTime: deadmanTimeSec,
									},
								],
							});
							dispatcher(setCredentialFunctionsArrayAction(newTableData));

							if (credentialFunction === CredentialFunctionEnum.CONTROL_ZONE) {
								const filteredData = filterLocalControllers(newTableData, controllers);
								if (filteredData.length > 0) {
									setController(filteredData[0].ControllerId);
								} else {
									setController(-1);
								}
							}
						}
					}
				}
				setControlZone(-1);
				break;
		}
	};

	const onEntityAction = async (id: number, isMaster: boolean) => {
		let dgs: DoorGroup[] = [];
		let localDgs: DoorGroup[] = [];

		try {
			const doorGroups = await enrollmentApi.getOperatorDoorGroups();
			dgs = doorGroups?.length > 0 ? [...defDgs, ...doorGroups.sort((a, b) => a.Name.localeCompare(b.Name))] : defDgs;

			if (credentialFunctions?.length > 0) {
				localDgs = filterDoorGroups(credentialFunctions, dgs);
			} else {
				localDgs = dgs;
			}

			const selectedDG = localDgs.find(dg => dg.DoorGroupId === id && dg.IsMasterDoorGroup === isMaster);

			batch(() => {
				dispatcher(setFunctionTabDgs(dgs));
				setLocalDoorGroups(localDgs);
				if (selectedDG) {
					const dgValue = buildUniqueIdDoorGroup(CredentialFunctionEnum.DOOR_GROUP, selectedDG);
					setDoorGroup(dgValue);
				}
			});
		} catch (error) {
			Logger.writeErrorLog(error);
		} finally {
			batch(() => {
				closeDGModal();
			});
		}
	};

	const expandedRowRender = (record: CredentialFunctionsDataType) => {
		return <DoorGroupInfoTable doorGroupId={record.HostZone} hasPermission={record.HasObjectPermission && doorGroupComponentPermission.canView} />;
	};

	const handleOnSetFunctionGroupModalVisible = async () => {
		try {
			const res: FunctionGroup[] = await enrollmentApi.getOperatorFunctionGroups();
			if (res && res.length > 0) {
				const fgs = [...defFgs, ...res];
				dispatcher(setFunctionTabFgs(fgs));
				setLocalFunctionGroups(filterLocalFunctionGroups(credentialFunctions, fgs));
			}
		} catch (error) {
			Logger.writeErrorLog(error);
		}
	};

	const closeDGModal = () => {
		batch(() => {
			setDoorGroup('');
			setDoorModal(DoorModalType.None);
			deviceCtrlDispatcher(setDefaultTablePaginationSetting());
		});
	};

	const closeFGModal = async (toggleModal: boolean) => {
		setFunctionGroupModal(toggleModal);
		setFunctionGroup(-1);
		await handleOnSetFunctionGroupModalVisible();
		deviceCtrlDispatcher(setDefaultTablePaginationSetting());
	};

	const linkToChkChanged = () => {
		if (credentialTemplates?.length > 0) {
			if (linkToCredential) {
				setIsTabDisabled(true);
			} else {
				setIsTabDisabled(false);
			}
		}
	};

	const setControllerIdState = (controllerId: number): void => {
		getControlZones(controllerId);
		setController(controllerId);
	};

	const isAccessFunction = (functionIndex: number): boolean => {
		if (
			// Access
			functionIndex === 1 ||
			functionIndex === 2 ||
			functionIndex === 3 ||
			functionIndex === 34 ||
			functionIndex === 37 ||
			functionIndex === 39 ||
			functionIndex === 40 ||
			// Password
			functionIndex === 20 ||
			functionIndex === 21 ||
			functionIndex === 22 ||
			functionIndex === 23 ||
			functionIndex === 24 ||
			// Special
			functionIndex === 25 ||
			functionIndex === 26 ||
			functionIndex === 27
		) {
			return true;
		}

		return false;
	};

	const getErrorMessageForConflictControllers = (
		conflicts: {
			controllerId: number;
			functionModel: FunctionModel;
			functionCategory: FunctionCategory;
		}[]
	): ConflictMessage[] => {
		const messages: ConflictMessage[] = conflicts.map<ConflictMessage>(conflicted => {
			const { controllerId, functionModel, functionCategory } = conflicted;
			let descriptorMsg = `${functionCategory.DefaultCategoryName}`;
			const controller = controllers.find(x => x.ControllerId === controllerId);
			const catName = functionCategories.find(fc => fc.FunctionCategoryIndex === functionCategory.FunctionCategoryIndex)?.DefaultCategoryName;
			return {
				CategoryName: catName,
				FunctionName: functionModel.DefaultFunctionName,
				ControllerName: controller.ControllerName,
				Description: descriptorMsg,
			};
		});

		return messages;
	};

	const checkConflicts = (
		dataSource: Partial<CredentialFunction>[],
		newItem: {
			key: string;
			functionModel: FunctionModel;
			functionCategory: FunctionCategory;
			controllerIds: number[];
		}
	): boolean => {
		let hasConflicts: boolean = false;
		const conflicts: {
			controllerId: number;
			functionModel: FunctionModel;
			functionCategory: FunctionCategory;
		}[] = [];

		const conflictedItems = dataSource.filter(x => {
			if (x.ControllerIds?.length > 0) {
				return x.ControllerIds?.some(y => newItem.controllerIds.includes(y));
			} else {
				return x.Zones?.some(y => newItem.controllerIds.includes(y.ControllerId));
			}
		});
		for (const controllerId of newItem.controllerIds) {
			const currentItemsCount = conflictedItems
				.flatMap(x => {
					if (x.ControllerIds?.length > 0) {
						return x.ControllerIds;
					} else {
						return x.Zones?.map(y => y.ControllerId);
					}
				})
				.filter(y => y === controllerId).length;
			for (const existing of conflictedItems) {
				if (
					(existing.ControllerIds?.length > 0 && existing.ControllerIds.includes(controllerId)) ||
					(existing.Zones?.length > 0 && existing.Zones.findIndex(x => x.ControllerId === controllerId) !== -1)
				) {
					if (OverlapAccessFlag) {
						if (currentItemsCount) {
							const { functionModel } = newItem;
							if (
								functionModel.FunctionIndex !== existing.FunctionIndex ||
								(functionModel.FunctionIndex === existing.FunctionIndex && !isAccessFunction(functionModel.FunctionIndex)) ||
								(functionModel.FunctionIndex === existing.FunctionIndex &&
									isAccessFunction(existing.FunctionIndex) &&
									currentItemsCount > noOfCustomAccessZones)
							) {
								hasConflicts = true;
								let item = conflicts.find(x => x.controllerId === controllerId && x.functionModel.FunctionIndex === existing.FunctionIndex);
								if (!item) {
									item = {
										functionCategory: {
											CategoryName: existing.FunctionName,
											DefaultCategoryName: existing.Name,
											FunctionCategoryIndex: existing.FunctionCategory,
										},
										functionModel: {
											DefaultFunctionName: existing.FunctionName,
											FunctionIndex: existing.FunctionIndex,
											FunctionName: existing.FunctionName,
										},
										controllerId,
									};
									conflicts.push(item);
								} else {
									item.functionCategory.DefaultCategoryName += `, ${existing.Name}`;
								}
							}
						}
					} else {
						if (currentItemsCount && existing.key !== newItem.key) {
							hasConflicts = true;
							let item = conflicts.find(x => x.controllerId === controllerId && x.functionModel.FunctionIndex === existing.FunctionIndex);
							if (!item) {
								item = {
									functionCategory: {
										CategoryName: existing.FunctionName,
										DefaultCategoryName: existing.Name,
										FunctionCategoryIndex: existing.FunctionCategory,
									},
									functionModel: {
										DefaultFunctionName: existing.FunctionName,
										FunctionIndex: existing.FunctionIndex,
										FunctionName: existing.FunctionName,
									},
									controllerId,
								};
								conflicts.push(item);
							} else {
								item.functionCategory.DefaultCategoryName += `, ${existing.Name}`;
							}
						}
					}
				}
			}
		}

		if (hasConflicts) {
			setConflictMessages(getErrorMessageForConflictControllers(conflicts));
			setShowConflictMsgModal(true);
		}

		return hasConflicts;
	};

	const getControlZones = async (controllerId: number) => {
		try {
			let controlZones: ControlZone[] = [];
			const currentControlZones = new Map(fetchedControlZones);
			if (currentControlZones.has(controllerId) && currentControlZones.get(controllerId)?.length > 0) {
				controlZones = currentControlZones.get(controllerId);
			} else {
				controlZones = await deviceAdminApi.retrieveControlZones(controllerId);
				currentControlZones.set(controllerId, controlZones || []);
				setFetchedControlZones(currentControlZones);
			}
			dispatcher(setControlZonesArrayAction(controlZones));
		} catch (err) {
			Logger.writeErrorLog(err);
			dispatcher(setControlZonesArrayAction([]));
		}
	};

	const handleOnChangeFunctionCategory = (stringValue: string) => {
		const value: number = Number(stringValue);
		const selectedFunctionCategory = functionCategories.find(fc => fc.FunctionCategoryIndex === value);
		setFunctionCategory(selectedFunctionCategory.FunctionCategoryIndex);
		handleOnChangeFunction(selectedFunctionCategory.Functions[0].FunctionIndex?.toString(), selectedFunctionCategory.FunctionCategoryIndex);
	};

	const handleOnChangeFunction = (stringValue: string, localFunctionCategory: number) => {
		if (localFunctionCategory === -1) {
			localFunctionCategory = functionCategory;
		}

		const value: number = Number(stringValue);
		if (localFunctionCategory === CredentialFunctionCategory.Special && (value === 29 || value === 41)) {
			setController(-1);
		}
		if (
			localFunctionCategory === CredentialFunctionCategory.Alarm ||
			localFunctionCategory === CredentialFunctionCategory.Relay ||
			(localFunctionCategory === CredentialFunctionCategory.Special && value === 28)
		) {
			const filteredData = filterLocalControllers(credentialFunctions, controllers);
			if (filteredData.length > 0) {
				setControllerIdState(filteredData[0].ControllerId);
			} else {
				setController(-1);
			}
		}
		setFunctionS(value);
	};

	const removeTableData = (idx: number) => {
		if (credentialFunctions && credentialFunctions.length > 0) {
			let newTableData = [...credentialFunctions];
			newTableData.splice(idx, 1);
			dispatcher(setCredentialFunctionsArrayAction(newTableData));
			const tempDgs = filterDoorGroups(newTableData, doorGroups);
			const tempFgs = filterLocalFunctionGroups(newTableData, functionGroups);
			setLocalDoorGroups(tempDgs);
			setLocalFunctionGroups(tempFgs);
			setExpandedRowKeys([]);
		}
	};

	const handleOnChangeDoorGroup = (value: string) => {
		const [_, doorGroupId] = value.split('%');
		const id = parseInt(doorGroupId);
		if (id < -1) {
			setDoorModal(id);
		} else {
			closeDGModal();
			setDoorGroup(value);
			if (id > 0) {
				dispatcher(setRootModelPropertyAction({ hasChanged: true }));
			}
		}
	};

	const handleOnChangeFunctionGroup = (value: number) => {
		if (value < -1) {
			setFunctionGroupModal(true);
		} else {
			setFunctionGroup(value);
			dispatcher(setRootModelPropertyAction({ hasChanged: true }));
		}
	};

	const columns: ColumnsProps<Partial<CredentialFunction>>[] = [
		{
			title: '',
			dataIndex: 'expand',
			width: 50,
			render: (_, record: CredentialFunctionsDataType) => {
				const isExpanded = expandedRowKeys.includes(record.key);
				const functionCategory = record.FunctionCategory;
				const functionIndex = record.FunctionIndex;

				let isSpecialDoor: boolean = functionCategory === CredentialFunctionCategory.Special && [25, 26, 27].includes(functionIndex);
				if (
					!record.IsMasterDoorGroup &&
					(functionCategory === CredentialFunctionCategory.Access || functionCategory === CredentialFunctionCategory.Password || isSpecialDoor)
				) {
					return (
						<Button
							disabled={!(record.HasObjectPermission && doorGroupComponentPermission.canView)}
							className={styles.expandIcon}
							type='text'
							title={getPermissionErrorMessage(record.HasObjectPermission && doorGroupComponentPermission.canView)}
							onClick={() => {
								if (isExpanded) {
									setExpandedRowKeys([]);
								} else {
									setExpandedRowKeys([record.key]);
								}
							}}
							icon={isExpanded ? <MinusSquareOutlined /> : <PlusSquareOutlined />}
						/>
					);
				}
				return null;
			},
		},
		{
			...buildColumn(_('Function'), 'FunctionName', '15em', 'start'),
		},
		{
			...buildColumn(_('GroupZoneName'), 'DisplayName', 'auto', 'start'),
		},
		{
			...buildColumn(timeColumnName, 'Zones', `${timeColumnName.length + 1}ch`, 'start'),
			render: (Zones: PanelZone[], record: CredentialFunctionsDataType) => {
				let content: string | number = '';
				if (record.FunctionCategory == 4 && record.FunctionIndex === 28 && Zones && Zones.length > 0) {
					content = Zones[0].DeadmanTime;
				}

				return <span>{content}</span>;
			},
		},
		{
			...buildColumn('', '', '30px', 'end'),
			render: (_, record: CredentialFunctionsDataType, idx: number) =>
				record.IsMasterDoorGroup && (
					<Button
						id={`credentialViewMasterDoorGroup-${idx}`}
						className={styles.icon}
						disabled={!(record.HasObjectPermission && masterDoorGroupComponentPermission.canView)}
						onClick={() => setMasterDoorGroupInfo({ id: record.MasterDoorGroupId, name: record.Name, visible: true })}
						icon={<GreenEyeIcon style={{ width: 24, height: 24 }} />}
						key='eye'
						type='text'
						title={getPermissionErrorMessage(record.HasObjectPermission && masterDoorGroupComponentPermission.canView)}
					/>
				),
		},
		{
			...buildColumn('', '', '30px', 'end'),
			render: (_, record: CredentialFunctionsDataType, idx: number) =>
				!isTabDisabled && (
					<Button
						disabled={!record.HasObjectPermission}
						title={record.HasObjectPermission ? '' : record.HostZone < 0 ? DeleteCZNotAllowed : DeleteFunctionNotAllowed}
						className={styles.icon}
						onClick={() => removeTableData(idx)}
						icon={<TrashCircleIcon style={{ width: 24, height: 24 }} />}
						key='trash'
						type='text'
					/>
				),
		},
	];

	const controlZoneDropdownComponent = (handleOnChange: (value: number) => void) => (
		<>
			<label htmlFor='controlZone'>{_('ControlZone')}:</label>
			<Select
				notUseCurrentParse
				id='controlZone'
				className={styles.select}
				disabled={isTabDisabled}
				getPopupContainer={(trigger: HTMLElement) => trigger.parentElement}
				onChange={handleOnChange}
				options={
					controller !== -1
						? controlZones?.map(cz => ({
								key: cz.ControlZoneIndex,
								label: cz.ControlZoneName,
								value: cz.ControlZoneIndex,
						  }))
						: []
				}
				style={{ width: 505 }}
				value={controlZone > -1 ? controlZone : ''}
				showSearch={true}
				filterOption={filterOption}
				virtual={true}
			/>
		</>
	);

	const controllerDropdownComponent = (credFunctions: Partial<CredentialFunction>[], handleOnChange: (value: number) => void) => {
		const filteredData = filterLocalControllers(credFunctions, controllers);

		return (
			<>
				<label htmlFor='controller'>{_('Controller')}:</label>
				<Select
					notUseCurrentParse
					id='controller'
					className={styles.select}
					disabled={isTabDisabled}
					getPopupContainer={(trigger: HTMLElement) => trigger.parentElement}
					onChange={handleOnChange}
					options={filteredData.map(controller => ({
						key: controller.ControllerId,
						label: controller.ControllerName,
						value: controller.ControllerId,
					}))}
					style={{ width: 505 }}
					value={controller > -1 ? controller : ''}
					showSearch={true}
					filterOption={filterOption}
					virtual={true}
				/>
			</>
		);
	};

	const handleOnChangeControlZone = (credentialFunction: CredentialFunctionEnum, value: number): void => {
		setControlZone(value);
		if (functionCategories?.length > 0 && value > -1) {
			addCredentialFunction({
				credentialFunction,
				controlZoneIndex: value,
				controllerIndex: controller,
			});
		}
		dispatcher(setRootModelPropertyAction({ hasChanged: true }));
	};

	const buildUniqueIdDoorGroup = (credentialFunction: CredentialFunctionEnum, doorGroup: DoorGroup): string => {
		return `${credentialFunction}%${doorGroup.DoorGroupId}%${doorGroup.IsMasterDoorGroup}`;
	};

	const buildUniqueIdFunctionGroup = (credentialFunction: CredentialFunctionEnum, functionGroup: FunctionGroup): string => {
		return `${credentialFunction}%${functionGroup.FunctionGroupId}`;
	};

	const buildUniqueIdIndexedCommand = (credentialFunction: CredentialFunctionEnum, indexedCommand: SelectOption): string => {
		return `${credentialFunction}%${indexedCommand.Id}`;
	};

	const buildUniqueIdThreatLevel = (credentialFunction: CredentialFunctionEnum, threatLevel: ThreatLevel): string => {
		return `${credentialFunction}%${threatLevel.ThreatId}`;
	};

	const buildUniqueIdDefault = (credentialFunction: CredentialFunctionEnum, index: number, controllerId: number): string => {
		return `${credentialFunction}%${index}%${controllerId}`;
	};

	const renderControlZoneLayout = (): JSX.Element => {
		let dd: JSX.Element = null;
		if (functionCategory === CredentialFunctionCategory.Relay || functionCategory === CredentialFunctionCategory.Alarm) {
			dd = (
				<>
					<div className={cx(styles.sections, styles.functionSections)}>{controllerDropdownComponent(credentialFunctions, setControllerIdState)}</div>
					<div className={cx(styles.sections, styles.functionSections)}>
						{controlZoneDropdownComponent((value: number) => handleOnChangeControlZone(CredentialFunctionEnum.CONTROL_ZONE, value))}
					</div>
				</>
			);
		} else if (functionCategory === 5 && functionGroups?.length > 0) {
			dd = (
				<div className={cx(styles.sections, styles.functionSections)}>
					<label htmlFor='FunctionGroup'>{_('FunctionGroup')}:</label>
					<div>
						<Select
							notUseCurrentParse
							id='FunctionGroup'
							className={styles.select}
							disabled={isTabDisabled}
							getPopupContainer={(trigger: HTMLElement) => trigger.parentElement}
							onChange={handleOnChangeFunctionGroup}
							options={localFunctionGroups.map(fg => ({
								key: fg.FunctionGroupId,
								label: fg.Name || ' ',
								value: fg.FunctionGroupId,
							}))}
							style={{ width: 505 }}
							value={functionGroup}
							showSearch={true}
							filterOption={filterOption}
							virtual={true}
						/>
					</div>
				</div>
			);
		} else if (functionCategory == 4 && (functionS === 28 || functionS === 29 || functionS === 41)) {
			switch (functionS) {
				case 28: // Deadman timer
					dd = (
						<div>
							<div className={cx(styles.sections, styles.functionSections)}>
								{controllerDropdownComponent(credentialFunctions, setControllerIdState)}
							</div>
							<div className={styles.deadmanTimer}>
								{controlZoneDropdownComponent((value: number) => {
									handleOnChangeControlZone(CredentialFunctionEnum.DEADMAN_TIMER, value);
									setControllerIdState(-1);
								})}
								<label htmlFor='deadmanTimerDropdown'>
									{_('Time')} ({_('Sec')}):
								</label>
								<InputNumber
									className={styles.inputNumber}
									disabled={isTabDisabled}
									id='deadmanTimerDropdown'
									max={MAX_DEADMAN_SECONDS}
									min={0}
									onChange={(value: number) => {
										setDeadmanTime(value);
									}}
									value={deadmanTime}
									formatter={noneDecimalFormatter}
								/>
							</div>
						</div>
					);
					break;

				case 29: // Indexed command
					dd = (
						<div className={styles.indexedCommand}>
							<div className={cx(styles.sections, styles.functionSections)}>
								<label htmlFor='indexedCommandDropdown'>{_('IndexedCommand')}:</label>
								<Select
									notUseCurrentParse
									id='indexedCommandDropdown'
									className={styles.select}
									disabled={isTabDisabled}
									getPopupContainer={(trigger: HTMLElement) => trigger.parentElement}
									onChange={(value: number) => setIndexedCommandId(value)}
									options={IndexedCommandsOptions.map(option => ({
										key: option.Id,
										label: option.Name,
										value: option.Id,
									}))}
									style={{ width: 505 }}
									value={indexedCommandId}
									showSearch={true}
									filterOption={filterOption}
									virtual={true}
								/>
							</div>
							<div className={cx(styles.sections, styles.functionSections)}>
								{controllerDropdownComponent(
									credentialFunctions,
									(value: number) => {
										setControllerIdState(value);
										addCredentialFunction({
											credentialFunction: CredentialFunctionEnum.INDEXED_COMMAND,
											controllerIndex: value,
										});
									}
								)}
							</div>
						</div>
					);
					break;

				case 41: // Threat level
					dd = (
						<div>
							<div className={cx(styles.sections, styles.functionSections)}>
								<label htmlFor='threatLevelDropdown'>{_('SetThreatLevel')}:</label>
								<Select
									notUseCurrentParse
									id='threatLevelDropdown'
									className={styles.select}
									disabled={isTabDisabled}
									getPopupContainer={(trigger: HTMLElement) => trigger.parentElement}
									onChange={(value: number) => setThreatLevel(value)}
									options={threatLevels.map((threatLevel: ThreatLevel) => ({
										key: threatLevel.ThreatId,
										label: threatLevel.Name,
										value: threatLevel.ThreatId,
									}))}
									style={{ width: 505 }}
									value={threatLevel}
									showSearch={true}
									filterOption={filterOption}
									virtual={true}
								/>
							</div>
							<div className={cx(styles.sections, styles.functionSections)}>
								{controllerDropdownComponent(
									credentialFunctions,
									(value: number) => {
										setControllerIdState(value);
										addCredentialFunction({
											credentialFunction: CredentialFunctionEnum.THREAT_LEVEL,
											controllerIndex: value,
										});
									}
								)}
							</div>
						</div>
					);
					break;
			}
		} else {
			dd = (
				<div className={cx(styles.sections, styles.functionSections)}>
					<label htmlFor='doorGroup'>{_('DoorGroup')}:</label>
					<div>
						<Select
							className={styles.select}
							id='doorGroup'
							disabled={isTabDisabled}
							getPopupContainer={(trigger: HTMLElement) => trigger.parentElement}
							onChange={handleOnChangeDoorGroup}
							options={localDoorGroups.map((dg: DoorGroup) => {
								const key = buildUniqueIdDoorGroup(CredentialFunctionEnum.DOOR_GROUP, dg);
								return {
									key,
									label: dg.Name || ' ',
									title: dg.Name,
									value: key,
								};
							})}
							style={{ width: 505 }}
							value={doorGroup}
							showSearch={true}
							filterOption={filterOption}
							virtual={true}
						/>
					</div>
				</div>
			);
		}
		return (
			<div ref={ddContainerRef} id='ddContainer'>
				{dd}
			</div>
		);
	};

	const getFunctionOptions = (): FunctionModel[] => {
		let options: FunctionModel[] = [];
		if (functionCategories?.length > 0 && functionCategory > -1) {
			const lFunctionCategory = functionCategories.find(fc => fc.FunctionCategoryIndex === functionCategory);
			if (lFunctionCategory) options = lFunctionCategory.Functions;
		}
		return options;
	};

	const renderDoorModal = (): JSX.Element => {
		if (doorModal === DoorModalType.DoorGroup) {
			return <DoorModal onEntityAction={onEntityAction} onSetVisible={closeDGModal} setShouldResetSearchColumn={() => {}} />;
		}
		if (doorModal === DoorModalType.MasterDoorGroup) {
			return <DoorMasterModal onEntityAction={onEntityAction} onSetVisible={closeDGModal} setShouldResetSearchColumn={() => {}} />;
		}
		return null;
	};

	const renderFunctionGroupModal = () => {
		if (functionGroupModal) {
			return <FunctionGroupModal functionGroupId={undefined} onSetVisible={closeFGModal} functionGroupName={''} />;
		}
	};

	return (
		<Spin spinning={isLoading} tip={`${_('Loading')}...`}>
			<div className={styles.containerFunctionTab}>
				{showConflictMsgModal && (
					<ControllerConflictWarningModal
						conflicts={conflictMessages}
						overlapAccessFlag={OverlapAccessFlag}
						cCMVersion={CCMVersionSupportsHRecord}
						customAccessZones={CustomAccessZones}
						onOkClick={() => {
							setShowConflictMsgModal(false);
							setConflictMessages([]);
						}}
					/>
				)}
				<div ref={outerContainerRef} className={styles.formFunctionTab} id='functionDDContainer'>
					<div ref={catContainerRef} id='catContainer' style={{ display: 'grid', gridTemplateColumns: '475px 475px' }}>
						<div>
							<div className={cx(styles.sections, styles.functionSections)}>
								<label htmlFor='functionCategory'>{_('FunctionCategory')}:</label>
								<Select
									className={styles.select}
									disabled={isTabDisabled}
									id='functionCategory'
									onChange={handleOnChangeFunctionCategory}
									options={functionCategories?.map(fc => ({
										key: fc.FunctionCategoryIndex?.toString(),
										label: fc.DefaultCategoryName,
										value: fc.FunctionCategoryIndex?.toString(),
									}))}
									style={{ width: 240 }}
									value={functionCategory?.toString()}
								/>
							</div>
							<div style={{ display: 'grid', gridTemplateColumns: '370px auto' }}>
								<div className={cx(styles.sections, styles.functionSections)}>
									<label htmlFor='functionS'>{_('Function')}:</label>
									<Select
										className={styles.select}
										disabled={isTabDisabled}
										id='functionS'
										onChange={value => handleOnChangeFunction(value?.toString(), -1)}
										options={getFunctionOptions().map(f => ({
											key: f.FunctionIndex?.toString(),
											label: f.DefaultFunctionName,
											value: f.FunctionIndex?.toString(),
										}))}
										style={{ width: 240 }}
										value={functionS?.toString()}
									/>
								</div>
							</div>
						</div>
						<div>
							<div style={{ marginTop: 7, minHeight: 33 }} />
							<div style={{ margin: '7px 0 0 -70px' }}>
								<label>{_('SelectFunctionMessage')}</label>
							</div>
						</div>
					</div>
					{renderControlZoneLayout()}
					<Table
						id='credentialFunctionsTable'
						className={`${styles.functionsTable} ${styles.mainTable}`}
						columns={columns}
						dataSource={credentialFunctions?.map((cf, idx: number) => {
							return { ...cf, key: idx };
						})}
						rowClassName={(record, index) => {
							if (expandedRowKeys.includes(record.key)) {
								return styles.expandedRow;
							}

							return index % 2 === 0 ? styles.odd : styles.even;
						}}
						pagination={false}
						bordered={false}
						size='small'
						style={{ width: 940, marginTop: 20 }}
						expandable={{
							expandedRowRender,
							expandedRowKeys,
							expandIconColumnIndex: -1,
							indentSize: 0,
							expandedRowClassName: () => styles.noPaddingExpandedRow,
						}}
						scroll={{ y: tableMaxHeight, x: 'max-content' }}
					/>
				</div>
				{renderDoorModal()}
				{renderFunctionGroupModal()}
				{masterDoorGroupInfo.visible && (
					<MasterDoorGroupInfoModal
						id={masterDoorGroupInfo.id}
						name={masterDoorGroupInfo.name}
						onClose={() => setMasterDoorGroupInfo(defaultMasterDoorGroupInfo)}
					/>
				)}
			</div>
		</Spin>
	);
};

export { CredentialTemplateModalFunctionsTab };
